/**
 * Reader factory shared by every moderation enum.
 *
 * The API serialises these enums as string names, while the database, the
 * stored procedures and the admin handoff notes refer to them by ordinal, and a
 * payload assembled by hand can carry either. One reader accepts both so no
 * call site has to guess which form it was handed.
 *
 * Ordinals are 1-based and follow the declaration order in `swagger.json`.
 */
export const createModerationEnumReader = <T extends string>(
  values: readonly T[],
): ((value: unknown) => T | null) => {
  const byLowercaseName = new Map<string, T>(
    values.map(value => [value.toLowerCase(), value]),
  );
  const byOrdinal = new Map<string, T>(
    values.map((value, index) => [String(index + 1), value]),
  );

  return (value: unknown): T | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? byOrdinal.get(String(value)) ?? null : null;
    }
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    return (
      byOrdinal.get(trimmed) ?? byLowercaseName.get(trimmed.toLowerCase()) ?? null
    );
  };
};
